import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagsContext } from '@/contexts/FeatureFlagsContext';

/**
 * Profile-page section that lets the user opt in to admin-managed feature
 * flags. Hidden by default for guests (the parent decides whether to render
 * it — guests have no User row, so flags can't persist). Empty state copy
 * surfaces when an admin has not defined any flags yet.
 */
export function FeatureFlagsSection(): JSX.Element {
  const { t } = useTranslation();
  const { loaded, registry, userFlags, setEnabled } = useFeatureFlagsContext();
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleToggle(key: string, next: boolean): Promise<void> {
    setError(null);
    setPendingKey(key);
    try {
      await setEnabled(key, next);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('experiments.saveError'));
    } finally {
      setPendingKey(null);
    }
  }

  if (!loaded) {
    return (
      <section data-testid="feature-flags-section" aria-busy="true">
        <h2>{t('experiments.title')}</h2>
        <p className="muted">{t('experiments.loading')}</p>
      </section>
    );
  }

  if (registry.length === 0) {
    return (
      <section data-testid="feature-flags-section">
        <h2>{t('experiments.title')}</h2>
        <p className="muted">{t('experiments.empty')}</p>
      </section>
    );
  }

  return (
    <section data-testid="feature-flags-section">
      <h2>{t('experiments.title')}</h2>
      <p className="muted">{t('experiments.blurb')}</p>
      <ul className="feature-flags-list" style={{ listStyle: 'none', padding: 0 }}>
        {registry.map((flag) => {
          const enabled = Boolean(userFlags[flag.key]);
          return (
            <li key={flag.id} className="feature-flags-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderTop: '1px solid var(--border, #2a2a2a)' }}>
              <input
                id={`ff-${flag.key}`}
                type="checkbox"
                checked={enabled}
                disabled={pendingKey === flag.key}
                onChange={(e) => { void handleToggle(flag.key, e.target.checked); }}
                style={{ marginTop: 4 }}
              />
              <label htmlFor={`ff-${flag.key}`} style={{ flex: 1, cursor: 'pointer' }}>
                <div style={{ fontWeight: 600 }}>{flag.label}</div>
                {flag.description && (
                  <div className="muted" style={{ marginTop: 4 }}>{flag.description}</div>
                )}
              </label>
            </li>
          );
        })}
      </ul>
      {error && (
        <p className="error" role="alert" style={{ marginTop: 12 }}>{error}</p>
      )}
    </section>
  );
}
